/**
 * DraftReportPreviewModal — read-only "Draft Report Preview".
 *
 * Opened from the top-bar Preview button. Renders the same PDF the
 * Download button produces (via the backend's download-pdf endpoint),
 * not a live DocxEditor view -- the embedded editor doesn't paint
 * footers (documented in `frontend/vendor/eigenpal/VENDORING.md`), so
 * a docx-based preview can't match the downloaded artifact.
 *
 * Caller fetches the PDF as a Blob URL (via `URL.createObjectURL`) and
 * passes it in as `pdfUrl`. Preview intentionally stays reachable even
 * without the report-download capability (MDRS-299/271 — doctors need to
 * check formatting before the one-way Finalise lock regardless of their
 * export rights), which means it must not hand out an actual download
 * affordance either. An `<iframe src={pdfUrl}>` used to do exactly that:
 * every browser's built-in PDF viewer chrome carries its own Save/Print
 * controls, so anyone could download the report through Preview even with
 * the download toggle off (MDRS-443). Rendering via react-pdf's canvas
 * (`PdfPreview`, shared with the file-preview surfaces) instead of a raw
 * iframe closes that off — no native viewer, no toolbar. A determined user
 * could still screenshot or print-to-PDF from the OS print dialog; that's
 * an accepted residual, not something app code can prevent.
 */
import { PdfPreview } from "@components/FilePreview/PdfPreview";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@components/ui/dialog";
import { Skeleton } from "@components/ui/skeleton";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Object URL for a freshly-fetched PDF Blob. `undefined` while the
   *  server is converting .docx → PDF (~few seconds), then set when the
   *  fetch resolves. */
  pdfUrl: string | undefined;
  examineeName?: string | null;
  claimNumber?: string | null;
  templateName?: string | null;
};

export const DraftReportPreviewModal = ({
  open,
  onOpenChange,
  pdfUrl,
  examineeName,
  claimNumber,
  templateName,
}: Props) => {
  const subParts = [
    examineeName ? `Examinee: ${examineeName}` : null,
    claimNumber ? `Claim: ${claimNumber}` : null,
    templateName ? `Template: ${templateName}` : null,
  ].filter(Boolean);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex h-[95vh] w-[95vw] max-w-[1200px] flex-col gap-0 overflow-hidden p-0 sm:max-w-[1200px]">
        <DialogHeader className="flex-shrink-0 border-b px-6 py-4 text-left">
          <DialogTitle>Draft Report Preview</DialogTitle>
          {subParts.length ? (
            <p className="mt-1 text-xs text-foreground-muted">
              {subParts.join(" • ")}
            </p>
          ) : null}
        </DialogHeader>

        <div className="min-h-0 flex-1 overflow-hidden bg-[#F5F5F7]">
          {!pdfUrl ? (
            <div className="space-y-3 p-6">
              <Skeleton className="h-8 w-1/3" />
              <Skeleton className="h-64 w-full" />
              <p className="text-xs text-foreground-muted">
                Generating preview… (this matches the Download PDF output)
              </p>
            </div>
          ) : (
            <PdfPreview key={pdfUrl} fileUrl={pdfUrl} className="h-full border-0" />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
